import { useMemo } from 'react';
import { Controller, useForm, type UseFormSetError } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { BackButton } from '@/components/common/BackButton';
import { PageTitle } from '@/components/layout/PageTitle';
import { PageToolbar } from '@/components/layout/PageToolbar';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { OptionSelect, type SelectOption } from '@/components/common/OptionSelect';
import { useConfirmBeforeClose } from '@/hooks/useConfirmBeforeClose';
import { useResetOnFieldChange } from '@/hooks/useResetOnFieldChange';
import {
  companyListQueryOptions,
  departmentListQueryOptions,
  designationListQueryOptions,
} from '@/features/organization/api/organization.api';
import { userTypesQueryOptions } from '../../api/userTypes.api';
import {
  USER_TYPE_GROUPS,
  NON_HIERARCHICAL_USER_TYPES,
  CATEGORY_USER_FIELDS,
  COMMON_USER_FIELDS,
  isDateUserField,
  isLookupUserField,
  type UserTypeGroup,
} from '../../constants/user.constants';
import { createUserSchema, type CreateUserPayload } from '../../types/user.types';
import { DateField } from './DateField';
import { TextField } from './TextField';
import { SelectField } from './SelectField';
import { USER_FORM_GRID_CLASS, type UserFormLookups } from './types';

interface UserFormProps {
  category: UserTypeGroup;
  onSubmit: (values: CreateUserPayload, setFieldError: UseFormSetError<CreateUserPayload>) => void;
  isPending: boolean;
  onCancel: () => void;
}

export function UserForm({ category, onSubmit, isPending, onCancel }: UserFormProps) {
  const typeIds = USER_TYPE_GROUPS[category];

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors, isDirty },
  } = useForm<CreateUserPayload>({
    resolver: zodResolver(createUserSchema),
    mode: 'onTouched',
    defaultValues: { userTypeId: typeIds[0] },
  });

  const selectedUserTypeId = watch('userTypeId');
  const selectedCompanyId = watch('companyId');

  const { askBeforeClose, discardChangesDialog } = useConfirmBeforeClose(isDirty);

  useResetOnFieldChange({
    watch,
    setValue,
    whenChanged: 'companyId',
    reset: ['departmentId'],
  });

  const { data: companies, isLoading: companiesLoading } = useQuery(companyListQueryOptions());
  const { data: departments, isLoading: departmentsLoading } = useQuery(
    departmentListQueryOptions(
      selectedCompanyId !== undefined ? { companyId: selectedCompanyId } : {},
    ),
  );
  const { data: designations, isLoading: designationsLoading } = useQuery(
    designationListQueryOptions(),
  );
  const { data: userTypes } = useQuery(userTypesQueryOptions());

  const userTypeOptions = useMemo<SelectOption[]>(
    () =>
      typeIds.map((id) => ({
        id,
        name: userTypes?.find((t) => t.id === id)?.name ?? `Type ${id}`,
      })),
    [typeIds, userTypes],
  );

  const lookups: UserFormLookups = {
    companies,
    companiesLoading,
    departments,
    departmentsLoading,
    designations,
    designationsLoading,
  };

  const formTitle = `Create ${category.charAt(0).toUpperCase() + category.slice(1)} User`;

  return (
    <>
      <div className="flex flex-col gap-6">
        <PageTitle leading={<BackButton onClick={() => askBeforeClose(onCancel)} />}>
          {formTitle}
        </PageTitle>
        <PageToolbar currentTitle={formTitle} />

        <Card>
          <CardContent>
            <form
              onSubmit={handleSubmit((values) => onSubmit(values, setError))}
              className="flex flex-col gap-6"
            >
              <div>
                <h3 className="text-sm font-semibold mb-4">Basic Information</h3>
                <div className={USER_FORM_GRID_CLASS}>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="userTypeId" className="gap-1">
                      <span>
                        User Type
                        <span className="text-destructive">*</span>
                      </span>
                    </Label>
                    <Controller
                      name="userTypeId"
                      control={control}
                      render={({ field }) => (
                        <OptionSelect
                          id="userTypeId"
                          options={userTypeOptions}
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Select user type"
                        />
                      )}
                    />
                    {errors.userTypeId && (
                      <p className="text-xs text-destructive">{errors.userTypeId.message}</p>
                    )}
                  </div>
                  {COMMON_USER_FIELDS.map((field) => (
                    <TextField key={field} name={field} register={register} errors={errors} />
                  ))}
                  <TextField name="password" register={register} errors={errors} type="password" />
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold mb-4">Additional Information</h3>
                <div className={USER_FORM_GRID_CLASS}>
                  {CATEGORY_USER_FIELDS[category].map((field) => {
                    if (
                      field === 'parentUserId' &&
                      NON_HIERARCHICAL_USER_TYPES.has(selectedUserTypeId)
                    )
                      return null;
                    if (isLookupUserField(field)) {
                      return (
                        <SelectField
                          key={field}
                          name={field}
                          control={control}
                          errors={errors}
                          userTypeId={selectedUserTypeId}
                          lookups={lookups}
                        />
                      );
                    }
                    if (isDateUserField(field)) {
                      return (
                        <DateField key={field} name={field} control={control} errors={errors} />
                      );
                    }
                    return (
                      <TextField key={field} name={field} register={register} errors={errors} />
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center justify-end pt-2">
                <Button type="submit" disabled={isPending}>
                  {isPending ? (
                    <>
                      <Loader2 className="size-4 animate-spin" /> Creating...
                    </>
                  ) : (
                    'Create User'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
      {discardChangesDialog}
    </>
  );
}
